import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import Tree, { RawNodeDatum } from "react-d3-tree";
import {
  GroupModel,
  Groups,
} from "../../../type/response/GroupManagementInitResponse";
import { createNodeData, StyledIconButton } from "../GroupManagement";

/**
 * 子グループ追加・削除ダイアログコンポーネント
 * - 対象グループに子グループを追加・解除し、ツリー形式でプレビュー表示する
 * - props: open (ダイアログ表示状態), onClose (閉じるコールバック),
 *          groupModel (編集対象グループ), groupModelList (グループ一覧),
 *          createOrUpdateGroup (保存コールバック)
 */
export default function EditGroupChildrenDialog({
  open,
  onClose,
  groupModel,
  groupModelList,
  createOrUpdateGroup,
  handleOpenEditGroupDialog,
}: {
  open: boolean;
  onClose: () => void;
  groupModel: GroupModel | null;
  groupModelList: GroupModel[];
  createOrUpdateGroup: (groupModelList: GroupModel[], idempotencyKey?: string) => Promise<string | null>;
  handleOpenEditGroupDialog: (open: boolean, parent: GroupModel | null) => void;
}) {
  const [model, setModel] = useState(groupModel);
  const [newChild, setNewChild] = useState<Groups | null>(null);
  const isSubmitting = useRef<boolean>(false);

  /** 冪等性キー：ダイアログを開くたびに新しいUUIDを生成し、閉じると破棄する */
  const idempotencyKeyRef = useRef<string>("");
  useEffect(() => {
    if (open) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
  }, [open]);

  useEffect(() => setModel(groupModel), [groupModel]);

  const isSameData = useMemo(() => {
    const oldChildren = groupModel?.groupChildren ?? null;
    const newChildren = model?.groupChildren ?? null;
    return (
      (!oldChildren && !newChildren) ||
      (!!oldChildren &&
        !!newChildren &&
        oldChildren.length === newChildren.length &&
        oldChildren
          .sort((a, b) => ((a.id ?? "") < (b.id ?? "") ? -1 : 1))
          .every(
            (v, i) =>
              v.id ===
              newChildren.sort((a, b) =>
                (a.id ?? "") < (b.id ?? "") ? -1 : 1
              )[i].id
          ))
    );
  }, [groupModel, model]);

  const data: RawNodeDatum = useMemo(() => {
    return model ? createNodeData([model])[0] : { name: "root" };
  }, [model]);

  // 変更内容を保存する（二重送信防止あり）
  const handleSave = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      if (model) {
        const result = (await createOrUpdateGroup([model], idempotencyKeyRef.current))?.split(",");
        if (result) onClose();
      }
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="lg" fullWidth>
      <DialogTitle>子グループ追加・削除</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", my: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Autocomplete
              options={groupModelList
                .filter((v) => v.groupVersion?.id === model?.groupVersion?.id)
                .map((v) => v.group)}
              getOptionLabel={(option) => option.name}
              onChange={(_, value) => setNewChild(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="グループ"
                  sx={{ width: 500 }}
                />
              )}
            />
            <Box>
              <Button
                disabled={!newChild}
                variant="contained"
                startIcon={<Add />}
                onClick={() =>
                  setModel((prev) =>
                    prev
                      ? {
                          ...prev,
                          groupChildren: newChild
                            ? [...(prev.groupChildren ?? []), newChild]
                            : prev.groupChildren,
                        }
                      : null
                  )
                }
              >
                追加
              </Button>
            </Box>
          </Box>
          <Box>
            {/* <Button onClick={() => handleOpenEditGroupDialog(true, groupModel)}>
              新規子グループを作成
            </Button> */}
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: (data.children?.length ?? 1) * 120,
            background: "#f1f1f1ff",
          }}
        >
          <Tree
            data={data}
            translate={{ x: 210, y: (data.children?.length ?? 1) * 60 }}
            nodeSize={{ x: 500, y: 120 }}
            pathFunc="step"
            draggable={false}
            zoomable={false}
            renderCustomNodeElement={({ nodeDatum }) => (
              <foreignObject width={420} height={200} x={-200} y={-40}>
                <Card sx={{ width: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box>
                        {nodeDatum.name !== model?.group.id && (
                          <Tooltip title="親子関係を解除">
                            <StyledIconButton
                              type="button"
                              color="error"
                              onClick={() =>
                                setModel((prev) => {
                                  if (!prev) return null;
                                  if (!prev.groupChildren) return prev;
                                  const newChildren = prev.groupChildren.filter(
                                    (v) => v.id !== nodeDatum.name
                                  );
                                  return {
                                    ...prev,
                                    groupChildren:
                                      newChildren && newChildren.length > 0
                                        ? newChildren
                                        : null,
                                  };
                                })
                              }
                            >
                              <Remove />
                            </StyledIconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box>
                        <Typography>
                          {nodeDatum.attributes?.groupName ?? ""}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </foreignObject>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button variant="outlined" onClick={() => onClose()}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave()}
            disabled={isSameData}
          >
            登録
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
